import type { MultiPoint, Polygon, Position } from "geojson";

/** Generación de archivos KML descargables a partir del GeoJSON que ya trae la
 *  finca. Es solo-cliente y no necesita librerías: KML y GeoJSON comparten el
 *  mismo orden de coordenadas (lon,lat[,alt]) y los anillos ya vienen cerrados
 *  en ambos formatos, así que la conversión es directa.
 *
 *  Para el camino inverso (leer un .kml/.kmz) existe un parser probado en el
 *  proyecto Cliente (src/shared/utils/kmlUtils.ts → parseKmlOrKmzToPolygons),
 *  que usa @tmcw/togeojson + JSZip. Acá no se porta porque no hace falta. */

const escapeXml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

/** Un anillo de GeoJSON al formato de <coordinates> de KML: tuplas
 *  "lon,lat,alt" separadas por espacios. La altura es opcional en GeoJSON;
 *  KML la admite ausente pero varios lectores la esperan, así que va 0. */
const ringToCoordinates = (ring: Position[]): string =>
  ring.map(([lon, lat, alt]) => `${lon},${lat},${alt ?? 0}`).join(" ");

/** Envuelve uno o más placemarks en un documento KML completo. */
export const buildKmlDocument = (documentName: string, placemarks: string[]): string =>
  `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(documentName)}</name>
${placemarks.join("\n")}
  </Document>
</kml>
`;

/** Atributo de `<ExtendedData>`: `name` es la clave técnica (QGIS/ArcGIS la leen
 *  como columna de la tabla de atributos), `label` la etiqueta que muestra el globo
 *  de Google Earth, y `value` el dato ya convertido a texto. */
export interface PlacemarkField {
  name: string;
  label: string;
  value: string;
}

/** `<ExtendedData>` del placemark. Vacío si no hay atributos, para no ensuciar el KML. */
const fieldsToExtendedData = (fields: PlacemarkField[]): string =>
  fields.length === 0
    ? ""
    : `
      <ExtendedData>
${fields
  .map(
    (field) =>
      `        <Data name="${escapeXml(field.name)}"><displayName>${escapeXml(field.label)}</displayName><value>${escapeXml(field.value)}</value></Data>`,
  )
  .join("\n")}
      </ExtendedData>`;

/** Un Polygon de GeoJSON a un <Placemark>. Igual que en GeoJSON, el primer
 *  anillo es el contorno exterior y los siguientes son huecos. Los `fields` van
 *  antes de la geometría, que es donde el esquema de KML ubica <ExtendedData>. */
export const polygonToPlacemark = (
  name: string,
  polygon: Polygon,
  fields: PlacemarkField[] = [],
): string => {
  const [outer, ...holes] = polygon.coordinates;
  const inner = holes
    .map(
      (ring) =>
        `        <innerBoundaryIs><LinearRing><coordinates>${ringToCoordinates(ring)}</coordinates></LinearRing></innerBoundaryIs>`,
    )
    .join("\n");
  return `    <Placemark>
      <name>${escapeXml(name)}</name>${fieldsToExtendedData(fields)}
      <Polygon>
        <outerBoundaryIs><LinearRing><coordinates>${ringToCoordinates(outer)}</coordinates></LinearRing></outerBoundaryIs>${inner ? "\n" + inner : ""}
      </Polygon>
    </Placemark>`;
};

/** Documento KML con un único polígono. */
export const polygonToKml = (documentName: string, polygon: Polygon): string =>
  buildKmlDocument(documentName, [polygonToPlacemark(documentName, polygon)]);

/** Documento KML con varios polígonos nombrados: un Placemark por elemento. */
export const polygonsToKml = (
  documentName: string,
  items: Array<{ name: string; polygon: Polygon; fields?: PlacemarkField[] }>,
): string =>
  buildKmlDocument(
    documentName,
    items.map((item) => polygonToPlacemark(item.name, item.polygon, item.fields)),
  );

/** Cantidad de vértices de un polígono (todos los anillos), para mostrar en la UI. */
export const countPolygonVertices = (polygon: Polygon): number =>
  polygon.coordinates.reduce((total, ring) => total + ring.length, 0);

/** Un punto suelto a `<Placemark>`. Sin `<ExtendedData>`: los puntos del conteo
 *  no traen atributos, solo la posición del árbol. */
const positionToPlacemark = (name: string, [lon, lat, alt]: Position): string =>
  `    <Placemark>
      <name>${escapeXml(name)}</name>
      <Point><coordinates>${lon},${lat},${alt ?? 0}</coordinates></Point>
    </Placemark>`;

/** Documento KML con un Placemark por punto del MultiPoint, numerados desde 1.
 *  Se usa para el conteo de árboles, que puede traer decenas de miles. */
export const multiPointToKml = (documentName: string, multiPoint: MultiPoint): string =>
  buildKmlDocument(
    documentName,
    multiPoint.coordinates.map((position, index) =>
      positionToPlacemark(`${index + 1}`, position),
    ),
  );

/** Dispara la descarga del KML en el navegador. */
export const downloadKml = (filename: string, kml: string): void => {
  const url = URL.createObjectURL(new Blob([kml], { type: "application/vnd.google-earth.kml+xml" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".kml") ? filename : `${filename}.kml`;
  link.click();
  URL.revokeObjectURL(url);
};

// Marcas diacríticas combinantes (las que deja `normalize("NFD")`). Se arma
// desde una cadena ASCII para no meter caracteres invisibles en el fuente.
const DIACRITICOS = new RegExp("[\\u0300-\\u036f]", "g");

/** Fecha local en `YYYY-MM-DD`: ordena alfabéticamente igual que cronológicamente y no
 *  tiene la ambigüedad día/mes de los formatos locales. Local y no UTC porque lo que se
 *  fecha es el día en que el usuario se llevó el archivo. */
const hoyIso = (): string => {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Nombre de archivo seguro: sin acentos, espacios ni caracteres de ruta, con la fecha
 *  de descarga al final para distinguir snapshots de la misma finca. */
export const kmlFileName = (prefix: string, nombre: string): string =>
  `${prefix}_${nombre
    .normalize("NFD")
    .replace(DIACRITICOS, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")}_${hoyIso()}.kml`;
